<template>
  <div>
    <SubCard
      v-if="!loading && !isError"
      :subCardCurrentDataLabel="subCardInfoBoxLabel"
      :subCardCurrentInfoText="subCardInfoBoxData"
      :subCardTitle="subCardTitle"
      class="deckungsumfang-card"
    >
      <template #main>
        <p v-if="isNot134Comfort" class="mb-24">
          <strong>{{ $t('insuranceCover.kaskodeckung.pleaseChooseCoverage') }}</strong>
          <br />
          {{ $t('insuranceCover.kaskodeckung.hint') }}
        </p>
        <p v-else class="mb-24">
          <strong>{{
            $t('insuranceCover.kaskodeckung.currentCoveragePreselected')
          }}</strong>
        </p>
        <div class="choose-kasko-radio-group">
          <Radiogroup
            :items="chooseKaskoItems"
            :styleOption="styleOption"
            :value="currentCoverage.comprehensiveCover"
            @input="kaskoChanged"
          >
          </Radiogroup>
        </div>

        <ChangeSelbstbeteiligung
          v-if="hasDeductibleArea"
          :current-selbstbeteiligung="currentCoverage.deductible"
          :selected-kasko="newCoverage.comprehensiveCover"
          :value="newCoverage.deductible"
          @input="extrasChanged"
        ></ChangeSelbstbeteiligung>

        <div class="choose-leistungsextras">
          <ChangeLeistungsextras
            :selected-kasko="newCoverage.comprehensiveCover"
            :teilkasko-product="teilkaskoProduct!"
            :value="newExtras"
            :vollkasko-product="vollkaskoProduct!"
            @input="extrasChanged"
          >
          </ChangeLeistungsextras>
        </div>
      </template>
    </SubCard>
    <div v-else-if="!loading && isError" id="insurance-cover-loading-error">
      {{ $t('insuranceCover.loadingError') }}
    </div>
    <Modal
      :centeredText="false"
      :showCloseIcon="true"
      :showModal="showUpgradeInfo"
      title="Anpassung leider nicht möglich"
      @closeModal="closeModalUpgradeInfo()"
    >
      <template #content>
        <div>
          Es tut uns leid, in Ihrer Vertragskonstellation können wir Ihnen diese
          Umstellung nicht anbieten. Bei Rückfragen stehen wir Ihnen gerne zur Verfügung.
          Sie erreichen unser Service-Team rund um die Uhr unter
          <a href="tel:+496971158500">069&nbsp;7115&nbsp;8500</a>.
        </div>
      </template>
    </Modal>
  </div>
</template>

<script lang="ts" setup>
import {
  InputStatus,
  Modal,
  Radiogroup,
  StyleOption,
  SubCard
} from 'de-zurich-ui-library'
import { onBeforeMount, onMounted, type PropType, type Ref, ref } from 'vue'
import { type MotorContract } from '@/types/motor/MotorContract'
import type { HaftpflichtExtras, KaskoExtras } from '@/types/policy/PolicyTypes'
import { useI18n } from 'vue-i18n'
import {
  ComprehensiveCover,
  type Deductible,
  type DeductibleOption,
  deductibleToString,
  type Extras,
  type InsuranceCover,
  type KaskoProduct
} from '@/typesTenant/MotorContractChange'
import ChangeSelbstbeteiligung from '@/componentsTenant/motor/cockpit/ChangeSelbstbeteiligung.vue'
import ChangeLeistungsextras from '@/componentsTenant/motor/cockpit/ChangeLeistungsextras.vue'
import { fetchComprehensiveProducts } from '@/stores/motorService'

interface Item {
  id: string
  label: string
}

interface AvailableKaskoProducts {
  availableFullyComprehensiveProduct: KaskoProduct
  availablePartiallyComprehensiveProduct: KaskoProduct
}

const props = defineProps({
  motorContract: {
    type: Object as PropType<MotorContract>,
    required: true
  },
  value: {
    type: Object as PropType<InsuranceCover>,
    required: true
  },
  vehicleAge: {
    type: Number,
    required: true
  }
})

const emit = defineEmits<{
  extrasChanged: [none: void]
  status: [status: InputStatus]
}>()

const { t } = useI18n()

const status: Ref<InputStatus> = ref<InputStatus>(InputStatus.DEFAULT)
const styleOption: Ref<StyleOption> = ref(StyleOption.SECONDARY)
const loading = ref(true)
const isError = ref(false)
const showUpgradeInfo = ref(false)
const isNot134Comfort = ref(true)
const hasDeductibleArea = ref(true)
const subCardTitle: Ref<string> = ref('')
const subCardInfoBoxLabel: Ref<string> = ref('')
const subCardInfoBoxData: Ref<string[]> = ref([])
const chooseKaskoItems: Ref<Item[]> = ref([])
const currentCoverage: Ref<InsuranceCover> = ref(props.value)
const newCoverage: Ref<InsuranceCover> = ref(props.value)
const newExtras: Ref<Extras> = ref(props.value.newExtras)
const KAUFPREISENTSCHAEDIGUNG_FUER_GEBRAUCHTWAGEN = ref(false)
const ERWEITERTE_NEUWERTENTSCHAEDIGUNG = ref(false)
const teilkaskoProduct: Ref<KaskoProduct | undefined> = ref()
const vollkaskoProduct: Ref<KaskoProduct | undefined> = ref()

onBeforeMount(() => {
  currentCoverage.value = {
    comprehensiveCover: props.value.comprehensiveCover,
    liabilityBasicDiscountYear: props.value.liabilityBasicDiscountYear,
    deductible: props.value.deductible,
    oldExtras: props.value.oldExtras,
    newExtras: props.value.newExtras
  }
  newCoverage.value.comprehensiveCover = currentCoverage.value.comprehensiveCover
  newCoverage.value.liabilityBasicDiscountYear =
    currentCoverage.value.liabilityBasicDiscountYear
  newCoverage.value.deductible = currentCoverage.value.deductible
  newCoverage.value.oldExtras = currentCoverage.value.oldExtras
  newCoverage.value.newExtras = currentCoverage.value.newExtras

  newExtras.value = newCoverage.value.newExtras

  if (
    currentCoverage.value.oldExtras.comprehensiveInsuranceProduct?.extras
      .KAUFPREISENTSCHAEDIGUNG_FUER_GEBRAUCHTWAGEN
  ) {
    KAUFPREISENTSCHAEDIGUNG_FUER_GEBRAUCHTWAGEN.value =
      currentCoverage.value.oldExtras.comprehensiveInsuranceProduct.extras.KAUFPREISENTSCHAEDIGUNG_FUER_GEBRAUCHTWAGEN.valueOf()
  }
  if (
    currentCoverage.value.oldExtras.comprehensiveInsuranceProduct?.extras
      .ERWEITERTE_NEUWERTENTSCHAEDIGUNG
  ) {
    ERWEITERTE_NEUWERTENTSCHAEDIGUNG.value =
      currentCoverage.value.oldExtras.comprehensiveInsuranceProduct?.extras.ERWEITERTE_NEUWERTENTSCHAEDIGUNG.valueOf()
  }

  created()
})

onMounted(() => {
  const tarif = props.motorContract.liabilityInsurance.tarif

  if (tarif) {
    fetchComprehensiveProducts(tarif)
      .then((availableKaskoProducts) => {
        teilkaskoProduct.value =
          availableKaskoProducts.availablePartiallyComprehensiveProduct
        vollkaskoProduct.value = availableKaskoProducts.availableFullyComprehensiveProduct
        setCurrentExtras()
        status.value = InputStatus.VALID
        emit('status', status.value)
      })
      .catch(() => {
        isError.value = true
        status.value = InputStatus.ERROR
      })
      .finally(() => {
        loading.value = false
        setTimeout(() => {
          handleUpgradeComprehensiveCover()
        }, 100)
      })
  } else {
    // Wenn kein Tarif vorhanden ist, setzen wir den Fehlerstatus
    isError.value = true
    status.value = InputStatus.ERROR
    loading.value = false
  }
})

function created(): void {
  subCardInfoBoxData.value = []
  chooseKaskoItems.value = [
    {
      id: ComprehensiveCover.NO_COMPREHENSIVE,
      label: t(
        'insuranceCover.kaskodeckung.' + ComprehensiveCover.NO_COMPREHENSIVE
      ).toString()
    },
    {
      id: ComprehensiveCover.PARTIALLY_COMPREHENSIVE,
      label: t(
        'insuranceCover.kaskodeckung.' + ComprehensiveCover.PARTIALLY_COMPREHENSIVE
      ).toString()
    },
    {
      id: ComprehensiveCover.FULLY_COMPREHENSIVE,
      label: t(
        'insuranceCover.kaskodeckung.' + ComprehensiveCover.FULLY_COMPREHENSIVE
      ).toString()
    }
  ]
  subCardTitle.value = t('motorCockpitForm.insuranceCoverGroup.title')
  subCardInfoBoxLabel.value = t('insuranceCover.currentInsuredCoverage')
  subCardInfoBoxData.value.push(
    [
      t(
        'insuranceCover.kaskodeckung.' + currentCoverage.value?.comprehensiveCover
      ).toString(),
      'Selbstbeteiligung ' + deductibleToString(currentCoverage.value?.deductible)
    ].join(' • ')
  )
  const haftpflichtExtras = selectedExtras2String(
    currentCoverage.value.newExtras.liabilityProduct.extras,
    'haftpflicht'
  )
  const kaskoExtras = selectedExtras2String(
    currentCoverage.value?.newExtras.comprehensiveInsuranceProduct?.extras,
    'kasko'
  )
  subCardInfoBoxData.value.push(
    'Zusatzabsicherung: ' +
      haftpflichtExtras +
      (haftpflichtExtras !== '' && kaskoExtras !== '' ? ', ' : '') +
      kaskoExtras
  )
}

function closeModalUpgradeInfo(): void {
  showUpgradeInfo.value = false
}

function openModalUpgradeInfo(): void {
  showUpgradeInfo.value = true
}

function handleUpgradeComprehensiveCover() {
  const productKey = props.value.oldExtras.liabilityProduct.productKey
  const productTarif = props.value.oldExtras.liabilityProduct.productTarif

  if (
    // Fahrzeug ist älter oder 9 Jahre
    props.vehicleAge >= 9 &&
    productKey === '134' &&
    productTarif === 'COMFORT'
  ) {
    // Headline(alt): Bitte wählen Sie den neuen Versicherungsschutz aus.
    // Headline(neu): Vorbelegt ist Ihr aktueller Versicherungsschutz
    isNot134Comfort.value = false

    // ENTFERNEN: SubHeadline "Vorbelegt ist Ihr aktueller Versicherungsschutz"
    if (props.value.comprehensiveCover === ComprehensiveCover.NO_COMPREHENSIVE) {
      hasDeductibleArea.value = false
      disablePartiallyComprehensive()
      disableFullyComprehensive()
    }
  }
  if (
    // Fahrzeug ist älter oder 9 Jahre
    props.vehicleAge >= 9 &&
    // Aktuell ist es in Teilkasko versichert
    props.value.comprehensiveCover === ComprehensiveCover.PARTIALLY_COMPREHENSIVE
  ) {
    hasDeductibleArea.value = true
    disableFullyComprehensive()
  }
}

function disablePartiallyComprehensive() {
  const cpContainer =
    globalThis.document.querySelector('#cp') ??
    globalThis.document.querySelector('#host')?.shadowRoot?.querySelector('#cp')

  const partCompr = cpContainer?.querySelector('#PARTIALLY_COMPREHENSIVE')
    ?.parentElement as HTMLDivElement
  ;(partCompr.firstChild as HTMLInputElement).disabled = true
  partCompr.classList.add('disabled')
  partCompr.addEventListener('click', openModalUpgradeInfo)
}

function disableFullyComprehensive() {
  const cpContainer =
    globalThis.document.querySelector('#cp') ??
    globalThis.document.querySelector('#host')?.shadowRoot?.querySelector('#cp')

  const fullCompr = cpContainer?.querySelector('#FULLY_COMPREHENSIVE')
    ?.parentElement as HTMLDivElement
  ;(fullCompr.firstChild as HTMLInputElement).disabled = true
  fullCompr.classList.add('disabled')
  fullCompr.addEventListener('click', openModalUpgradeInfo)
}

function extrasChanged(val: DeductibleOption | Deductible | Extras): void {
  if (
    (val && Object.prototype.hasOwnProperty.call(val, 'deductibleFullyComprehensive')) ||
    Object.prototype.hasOwnProperty.call(val, 'deductiblePartiallyComprehensive')
  ) {
    newCoverage.value.deductible = val as Deductible
  }
  if (val && Object.prototype.hasOwnProperty.call(val, 'value')) {
    newCoverage.value.deductible = (val as DeductibleOption).value
  }
  if (
    newExtras.value?.comprehensiveInsuranceProduct &&
    newExtras.value?.comprehensiveInsuranceProduct.extras &&
    val &&
    Object.prototype.hasOwnProperty.call(val, 'comprehensiveInsuranceProduct') &&
    (val as Extras).comprehensiveInsuranceProduct?.extras
  ) {
    Object.entries((val as Extras).comprehensiveInsuranceProduct!.extras).forEach(
      ([key, value]) => {
        newExtras.value!.comprehensiveInsuranceProduct!.extras[key as keyof KaskoExtras] =
          value
      }
    )

    Object.entries((val as Extras).liabilityProduct.extras).forEach(([key, value]) => {
      newExtras.value!.liabilityProduct!.extras[key as keyof HaftpflichtExtras] = value
    })
  }
  newCoverage.value.newExtras.liabilityProduct = newExtras.value!.liabilityProduct
  if (
    newCoverage.value.newExtras.comprehensiveInsuranceProduct &&
    newExtras.value?.comprehensiveInsuranceProduct
  ) {
    newCoverage.value.newExtras.comprehensiveInsuranceProduct.productKey =
      newExtras.value?.comprehensiveInsuranceProduct.productKey
    newCoverage.value.newExtras.comprehensiveInsuranceProduct.extras.ERSATZWAGEN =
      newExtras.value?.comprehensiveInsuranceProduct.extras.ERSATZWAGEN
    newCoverage.value.newExtras.comprehensiveInsuranceProduct.extras.FREIE_WERKSTATTWAHL =
      newExtras.value?.comprehensiveInsuranceProduct.extras.FREIE_WERKSTATTWAHL
    newCoverage.value.newExtras.comprehensiveInsuranceProduct.extras.GAP_DECKUNG =
      newExtras.value?.comprehensiveInsuranceProduct.extras.GAP_DECKUNG
    newCoverage.value.newExtras.comprehensiveInsuranceProduct.extras.RABATTSCHUTZ =
      newExtras.value?.comprehensiveInsuranceProduct.extras.RABATTSCHUTZ
    newCoverage.value.newExtras.comprehensiveInsuranceProduct.extras.TIERBISSFOLGESCHAEDEN_GEMAESS_AKB =
      newExtras.value?.comprehensiveInsuranceProduct.extras
        .TIERBISSFOLGESCHAEDEN_GEMAESS_AKB
    newCoverage.value.newExtras.comprehensiveInsuranceProduct.extras.VERSICHERUNGSSCHUTZ_BEI_GROBER_FAHRLAESSIGKEIT =
      newExtras.value?.comprehensiveInsuranceProduct.extras
        .VERSICHERUNGSSCHUTZ_BEI_GROBER_FAHRLAESSIGKEIT
    newCoverage.value.newExtras.comprehensiveInsuranceProduct.extras.ZUSAMMENSTOSS_MIT_ALLEN_TIEREN =
      newExtras.value?.comprehensiveInsuranceProduct.extras.ZUSAMMENSTOSS_MIT_ALLEN_TIEREN

    if (
      newCoverage.value?.comprehensiveCover ===
        currentCoverage.value?.comprehensiveCover &&
      newCoverage.value.newExtras.comprehensiveInsuranceProduct &&
      currentCoverage.value?.oldExtras.comprehensiveInsuranceProduct
    ) {
      newCoverage.value.newExtras.comprehensiveInsuranceProduct.extras.KAUFPREISENTSCHAEDIGUNG_FUER_GEBRAUCHTWAGEN =
        KAUFPREISENTSCHAEDIGUNG_FUER_GEBRAUCHTWAGEN.value
      newCoverage.value.newExtras.comprehensiveInsuranceProduct.extras.ERWEITERTE_NEUWERTENTSCHAEDIGUNG =
        ERWEITERTE_NEUWERTENTSCHAEDIGUNG.value
    } else {
      newCoverage.value.newExtras.comprehensiveInsuranceProduct.extras.KAUFPREISENTSCHAEDIGUNG_FUER_GEBRAUCHTWAGEN = false
      newCoverage.value.newExtras.comprehensiveInsuranceProduct.extras.ERWEITERTE_NEUWERTENTSCHAEDIGUNG = false
    }
  } else if (
    newCoverage.value.newExtras.comprehensiveInsuranceProduct === undefined &&
    newExtras.value?.comprehensiveInsuranceProduct?.extras
  ) {
    newCoverage.value.newExtras.comprehensiveInsuranceProduct =
      newExtras.value?.comprehensiveInsuranceProduct
  }
  emit('extrasChanged')
}

function selectedExtras2String(
  extras: KaskoExtras | HaftpflichtExtras | undefined,
  kaskodeckung: string
): string {
  if (extras === undefined) {
    return ''
  }
  return Object.entries(extras)
    .filter((entry) => entry[1])
    .map((entry) => entry[0])
    .map((label) => t('insuranceCover.extras.' + kaskodeckung + '.' + label))
    .join(', ')
}

function setForbiddenValuesFalse(previousValues: any, allowedKeys: any): any {
  const result: any = {}

  for (const key in previousValues) {
    if (Object.prototype.hasOwnProperty.call(previousValues, key)) {
      if (key in allowedKeys) {
        result[key] = previousValues[key]
      } else {
        result[key] = false
      }
    }
  }

  return result
}

function setValuesToExistingKeysOnly(
  objectWithCorrectKeys: any,
  objectWithCorrectValues: any
) {
  const result = { ...objectWithCorrectKeys }

  for (const key in result) {
    if (key in objectWithCorrectValues) {
      result[key] = objectWithCorrectValues[key]
    }
  }

  return result
}

function kaskoChanged(value: ComprehensiveCover): void {
  const previousValues = newExtras.value!.comprehensiveInsuranceProduct!.extras
  const allowedKeys = teilkaskoProduct.value!.extras
  let outcome = setForbiddenValuesFalse(previousValues, allowedKeys)
  if (value == ComprehensiveCover.NO_COMPREHENSIVE) {
    outcome = setForbiddenValuesFalse(previousValues, {})
  }

  if (value == ComprehensiveCover.PARTIALLY_COMPREHENSIVE) {
    newExtras.value!.comprehensiveInsuranceProduct!.productKey =
      teilkaskoProduct.value!.productKey

    // teilkaskoProduct.value muss angepasst werden in den values nicht den keys
    const teilkaskoKeysBefore = JSON.parse(JSON.stringify(teilkaskoProduct.value!.extras))
    teilkaskoProduct.value!.extras = setValuesToExistingKeysOnly(
      teilkaskoKeysBefore,
      outcome
    )
  } else if (value == ComprehensiveCover.FULLY_COMPREHENSIVE) {
    newExtras.value!.comprehensiveInsuranceProduct!.productKey =
      vollkaskoProduct.value!.productKey

    // vollkaskoProduct.value muss angepasst werden in den values nicht den keys
    const vollkaskoKeysBefore = JSON.parse(JSON.stringify(vollkaskoProduct.value!.extras))
    vollkaskoProduct.value!.extras = setValuesToExistingKeysOnly(
      vollkaskoKeysBefore,
      outcome
    )
  }

  const tmpObj = JSON.parse(JSON.stringify(newExtras.value))
  tmpObj.comprehensiveInsuranceProduct!.extras = outcome

  newExtras.value = tmpObj
  newCoverage.value.comprehensiveCover = value
}

function setCurrentExtras(): void {
  const kasko = props.motorContract.comprehensiveInsurance
  if (kasko !== undefined) {
    if (kasko.productKey === teilkaskoProduct.value?.productKey) {
      setExtras(teilkaskoProduct.value!.extras, kasko.extras)
    } else if (kasko.productKey === vollkaskoProduct.value?.productKey) {
      setExtras(vollkaskoProduct.value!.extras, kasko.extras)
    }
  }
}

function setExtras(target: KaskoExtras, source: KaskoExtras): void {
  for (const key of Object.keys(target)) {
    target[key as keyof KaskoExtras] = source[key as keyof KaskoExtras]
  }
}
</script>

<style lang="scss" scoped>
:deep(.choose-kasko-radio-group) {
  margin-bottom: 32px;

  .radio {
    display: flex;
    width: 100%;
    flex-wrap: wrap;
    justify-content: space-between;

    .radio__label {
      font-family: var(--f-primary-bold), serif;
    }
  }

  .disabled {
    .radio__label {
      color: var(--checkradio-label-color-disabled-label);
      cursor: default !important;

      &:hover {
        color: var(--checkradio-label-color-disabled-label) !important;
      }

      &:before {
        color: var(--checkradio-label-color-disabled-border-icon) !important;
        background-color: white !important;
        border-color: var(--checkbox-border-color-disabled) !important;
      }

      &:after {
        border-color: var(--checkradio-border-color-disabled) !important;
        border-width: var(--checkradio-border-width-disabled) !important;
        cursor: default !important;
      }
    }
  }
}

:deep(.choose-leistungsextras) {
  padding-top: 16px;

  .subCard {
    border: 0;
  }
}

#insurance-cover-loading-error {
  color: var(--accent-color-5);
}
</style>
